import React, { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Redo2, Target, Flame, Zap } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { TornCard } from '@branon-shared/ui/TornCard';
import { AmbientDoodles, SpillingDoodles } from '@branon-shared/ui/Doodle';
import { FONT_QUICKSAND } from '@branon-shared/tokens';

interface StudentProfile {
  student_id: string;
  student_code: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  display_name: string | null;
  name_verified: boolean;
  grade_level: number;
  section: string | null;
}

export interface HomeStats {
  streak: number;
  weeklyXP: number;
  todayConcepts: number;
  daysSinceLastEvent: number;
  lastConceptId?: number | null;
  lastConceptName?: string | null;
}

interface Props {
  studentId: string;
  homeStats?: HomeStats;
  /** Phase 8 (redesign): defaults to true so any other existing caller stays unaffected. */
  showGamification?: boolean;
  /** Rendered inside the TornCard's revealed paper panel, below the tear --
   *  lets Dashboard.tsx compose its intent-first cards as part of the same
   *  reveal instead of as a separate sibling block. */
  children?: ReactNode;
}

function getComebackMessage(days: number, firstName: string, lastConceptName?: string): string | null {
  if (days <= 1) return null;
  if (days <= 3) return `You haven't visited in ${days} days — your streak is at risk!`;
  if (days <= 7) return lastConceptName
    ? `It's been a while! Quick win: one question on ${lastConceptName}.`
    : `It's been a while! Pick up where you left off.`;
  return `Welcome back, ${firstName}! Let's warm up with something you already know well.`;
}

export const StudentWelcomeName: React.FC<Props> = ({ studentId, homeStats, showGamification = true, children }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(0);
  const [weeklyXP, setWeeklyXP] = useState(0);
  const [todayConcepts, setTodayConcepts] = useState(0);
  const [lastConcept, setLastConcept] = useState<{ id: number; name: string } | null>(null);
  const [daysSinceLastEvent, setDaysSinceLastEvent] = useState(0);

  useEffect(() => {
    fetchStudentProfile();
    if (homeStats) {
      setStreak(homeStats.streak);
      setWeeklyXP(homeStats.weeklyXP);
      setTodayConcepts(homeStats.todayConcepts);
      setDaysSinceLastEvent(homeStats.daysSinceLastEvent);
      if (homeStats.lastConceptId && homeStats.lastConceptName) {
        setLastConcept({ id: homeStats.lastConceptId, name: homeStats.lastConceptName });
      }
    } else {
      fetchFallbackStats();
    }
  }, [studentId, homeStats]);

  async function fetchStudentProfile() {
    setLoading(true);
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('student_get_own_profile', { p_student_id: studentId });
      if (!rpcError && rpcData?.length > 0) {
        const s = rpcData[0];
        setProfile({
          student_id: s.student_id, student_code: s.student_code,
          first_name: s.first_name, last_name: s.last_name,
          preferred_name: s.preferred_name, display_name: s.display_name,
          name_verified: s.name_verified, grade_level: s.grade_level, section: s.section,
        });
        return;
      }
      // Fallback to direct table query
      const { data, error } = await supabase
        .from('students')
        .select('id, student_code, first_name, last_name, preferred_name, name_verified, grade_level, section')
        .eq('id', studentId).single();
      if (!error && data) {
        const displayName = data.preferred_name ||
          (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : data.first_name) || null;
        setProfile({
          student_id: data.id, student_code: data.student_code,
          first_name: data.first_name, last_name: data.last_name,
          preferred_name: data.preferred_name, display_name: displayName,
          name_verified: data.name_verified || false, grade_level: data.grade_level, section: data.section,
        });
      }
    } catch { /* non-critical */ }
    finally {
      setLoading(false);
    }
  }

  async function fetchFallbackStats() {
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
      const { data: events } = await supabase
        .from('mastery_events')
        .select('created_at, concept_id')
        .eq('student_id', studentId)
        .gte('created_at', thirtyDaysAgo)
        .order('created_at', { ascending: false });

      if (!events?.length) return;

      const today = new Date(); today.setHours(0, 0, 0, 0);
      const eventDates = new Set(events.map(e => {
        const d = new Date(e.created_at); d.setHours(0, 0, 0, 0); return d.getTime();
      }));
      let s = 0;
      for (let i = 0; i <= 30; i++) {
        const d = new Date(today); d.setDate(d.getDate() - i);
        if (eventDates.has(d.getTime())) s++;
        else if (i > 0) break;
      }
      setStreak(s);

      const lastDate = new Date(events[0].created_at); lastDate.setHours(0, 0, 0, 0);
      setDaysSinceLastEvent(Math.floor((today.getTime() - lastDate.getTime()) / 86400000));

      const todayISO = today.toISOString().split('T')[0];
      const todayIds = new Set(events.filter(e => e.created_at.startsWith(todayISO)).map(e => e.concept_id));
      setTodayConcepts(todayIds.size);

      const weekAgo = Date.now() - 7 * 86400000;
      setWeeklyXP(events.filter(e => new Date(e.created_at).getTime() >= weekAgo).length * 10);

      const lastConceptId = events[0]?.concept_id;
      if (lastConceptId) {
        const { data: concept } = await supabase
          .from('concepts_plus1')
          .select('id, name')
          .eq('id', lastConceptId)
          .single();
        if (concept) setLastConcept({ id: concept.id, name: concept.name });
      }
    } catch { /* non-critical */ }
  }

  if (loading) return <div className="h-32 rounded-card bg-night animate-pulse" />;
  if (!profile) return null;

  const firstName = profile.display_name?.split(' ')[0] || 'there';
  const comebackMsg = showGamification ? getComebackMessage(daysSinceLastEvent, firstName, lastConcept?.name) : null;
  const dailyGoal = 3;
  const goalPct = Math.min(100, (todayConcepts / dailyGoal) * 100);

  return (
    <TornCard
      spill={<SpillingDoodles />}
      stage={
        <>
          <AmbientDoodles />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              {showGamification && (
                <div className="flex items-center gap-4 mb-2 flex-wrap">
                  {streak > 0 && (
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-coral">
                      <Flame className="w-4 h-4" />
                      {streak}-day streak
                    </span>
                  )}
                  {weeklyXP > 0 && (
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-marigold">
                      <Zap className="w-4 h-4" />
                      {weeklyXP} XP this week
                    </span>
                  )}
                </div>
              )}

              <p className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: FONT_QUICKSAND }}>
                {comebackMsg || `Hey ${firstName}! Ready to study?`}
              </p>

              {showGamification && (
                <div className="mt-3 mb-1 max-w-xs">
                  <div className="flex items-center justify-between text-xs text-white/50 mb-1">
                    <span>Daily goal</span>
                    <span>{todayConcepts} of {dailyGoal} concepts</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="bg-coral h-full transition-all duration-700" style={{ width: `${goalPct}%` }} />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 flex-wrap">
              {lastConcept && (
                <button
                  onClick={() => navigate(`/concept/${lastConcept.id}`)}
                  className="flex items-center gap-2 px-4 py-2 rounded-btn text-sm font-semibold text-white bg-white/10 border-2 border-white/20 hover:bg-white/15 transition-colors"
                >
                  <Redo2 className="w-4 h-4" />
                  Resume: {lastConcept.name.length > 20 ? lastConcept.name.slice(0, 20) + '…' : lastConcept.name}
                </button>
              )}
              <button
                onClick={() => navigate('/concepts')}
                className="flex items-center gap-2 px-4 py-2 rounded-btn text-sm font-bold text-white bg-coral shadow-sticker hover:opacity-90 transition-opacity"
              >
                <Target className="w-4 h-4" />
                Daily Q
              </button>
            </div>
          </div>
        </>
      }
    >
      {children}
    </TornCard>
  );
};

export default StudentWelcomeName;
